import { Link } from "react-router-dom";
import { ArrowUpRight, Clock, Mail, Phone } from "lucide-react";
import { COMPANY, PRODUCTS } from "@/data/content";
import { scrollToId } from "@/lib/api";
import { Reveal } from "./Reveal";

const Item = ({ icon: I, label, value, href, testid }) => {
  const Tag = href ? "a" : "div";
  return (
    <Tag href={href} data-testid={testid} className="group flex items-center justify-between border-b border-white/15 py-6 transition-colors hover:border-[var(--lime)]">
      <div className="flex items-center gap-4">
        <I className="h-5 w-5 text-[var(--lime)]" />
        <div>
          <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-sky-200">{label}</div>
          <div className="mt-1 font-display text-xl font-extrabold italic sm:text-2xl">{value}</div>
        </div>
      </div>
      {href && <ArrowUpRight className="h-5 w-5 transition-transform duration-300 group-hover:-translate-y-1 group-hover:translate-x-1" />}
    </Tag>
  );
};

export const Contact = () => (
  <section id="contact" data-testid="contact-section" className="grain relative overflow-hidden bg-[var(--blue)] py-24 text-white lg:py-32">
    <div className="absolute -bottom-40 -left-20 h-[500px] w-[500px] rounded-full bg-[var(--green)]/60 blur-[140px]" />
    <div className="relative mx-auto grid max-w-[1400px] grid-cols-1 gap-14 px-5 sm:px-8 lg:grid-cols-12">
      <Reveal className="lg:col-span-6">
        <h2 className="font-display text-5xl font-black italic leading-[0.95] tracking-tight sm:text-6xl lg:text-7xl">
          Ready when<br />you are.
        </h2>
        <p className="mt-6 max-w-md text-base leading-relaxed text-white/85 md:text-lg">
          Apply online in minutes or talk to a funding specialist today.
        </p>
        <button onClick={() => scrollToId("apply")} data-testid="contact-apply-btn" className="mt-9 rounded-full bg-white px-8 py-4 font-semibold text-[var(--ink)] transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-[var(--lime)]">
          Start my application
        </button>
      </Reveal>
      <Reveal delay={0.1} className="lg:col-span-6">
        <Item icon={Phone} label="Call" value={COMPANY.phone} href={COMPANY.phoneHref} testid="contact-phone" />
        <Item icon={Mail} label="Email" value={COMPANY.email} href={`mailto:${COMPANY.email}`} testid="contact-email" />
        <Item icon={Clock} label="Hours" value={COMPANY.hours} testid="contact-hours" />
      </Reveal>
    </div>
  </section>
);

export const Footer = () => (
  <footer data-testid="site-footer" className="bg-[var(--ink)] pb-10 pt-16 text-white">
    <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
      <div className="grid grid-cols-1 gap-10 md:grid-cols-12">
        <div className="md:col-span-5">
          <div className="inline-block rounded-2xl bg-white p-3"><img src="/brand/logo.webp" alt="613 Advance" className="h-16 w-auto" /></div>
          <p className="mt-6 max-w-sm text-sm leading-relaxed text-slate-300">Merchant cash advances and business financing for growing companies.</p>
        </div>
        <div className="md:col-span-4">
          <div className="font-mono text-xs uppercase tracking-[0.2em] text-sky-300">Products</div>
          <ul className="mt-4 grid grid-cols-1 gap-2 text-sm text-slate-300 sm:grid-cols-2">
            {PRODUCTS.map((p) => (
              <li key={p.id}><button onClick={() => scrollToId("products")} className="hover:text-white" data-testid={`footer-product-${p.id}`}>{p.name}</button></li>
            ))}
          </ul>
        </div>
        <div className="md:col-span-3">
          <div className="font-mono text-xs uppercase tracking-[0.2em] text-sky-300">Contact</div>
          <ul className="mt-4 space-y-2 text-sm text-slate-300">
            <li><a href={COMPANY.phoneHref} className="hover:text-white" data-testid="footer-phone">{COMPANY.phone}</a></li>
            <li><a href={`mailto:${COMPANY.email}`} className="hover:text-white" data-testid="footer-email">{COMPANY.email}</a></li>
            <li>{COMPANY.hours}</li>
          </ul>
        </div>
      </div>
      <div className="mt-14 flex flex-col gap-4 border-t border-white/10 pt-8 text-xs text-slate-400 md:flex-row md:items-center md:justify-between">
        <p className="max-w-3xl leading-relaxed">
          © {new Date().getFullYear()} 613 Advance. All funding is subject to approval and underwriting. A merchant cash advance is not a loan. Terms, rates and availability vary by product and business profile.
        </p>
        <div className="flex gap-6">
          <Link to="/about" data-testid="footer-about-link" className="hover:text-white">About us</Link>
          <Link to="/admin/login" data-testid="footer-admin-link" className="hover:text-white">Admin</Link>
        </div>
      </div>
    </div>
  </footer>
);
